import i18n from '@/utils/i18n';
import { getOperationName } from 'apollo-utilities';
import { gql, useMutation } from '@apollo/client';
import {
  NoteActionSheet_AddNoteActivityMutation,
  NoteActionSheet_AddNoteActivityMutationVariables,
  NoteActionSheet_AddNoteReminderMutation,
  NoteActionSheet_AddNoteReminderMutationVariables,
} from '@/types';
import { PAGINATED_CRM_ACTIVITIES_BY_CHAT_ID_QUERY } from './use-paginated-crm-activities-query';
import { ReminderTimestamp } from './use-note-reminder-form';
import { useToasts } from '@/contexts/toast-context';

export const ADD_NOTE_ACTIVITY_MUTATION = gql`
  mutation NoteActionSheet_AddNoteActivity(
    $chatUUID: uuid!
    $noteContent: String!
  ) {
    createNoteActivity(chat_id: $chatUUID, note_content: $noteContent) {
      crm_activity {
        uuid
        chat_uuid
      }
    }
  }
`;

export const ADD_NOTE_ACTIVITY_REMINDER_MUTATION = gql`
  mutation NoteActionSheet_AddNoteReminder(
    $crmActivityUUID: uuid!
    $chatUUID: uuid!
    $reminderDatetime: String!
  ) {
    createActivityReminder(
      activity_id: $crmActivityUUID
      chat_id: $chatUUID
      message: ""
      remind_at: $reminderDatetime
    ) {
      crm_activity_reminder {
        remind_at
        status
        uuid
      }
    }
  }
`;

export const useAddNoteActivity = (
  chatUUID: string,
  note: string,
  noteReminder?: ReminderTimestamp,
) => {
  const { showErrorToast } = useToasts();

  // When reminder is available, call refetchQueries after create note to avoid double refetching
  const refetchQueries = {
    refetchQueries: [
      getOperationName(PAGINATED_CRM_ACTIVITIES_BY_CHAT_ID_QUERY) as string,
    ],
  };
  const [createNoteActivity, { loading }] = useMutation<
    NoteActionSheet_AddNoteActivityMutation,
    NoteActionSheet_AddNoteActivityMutationVariables
  >(ADD_NOTE_ACTIVITY_MUTATION, !noteReminder ? refetchQueries : {});

  const [createNoteReminder, { loading: loadingReminder }] = useMutation<
    NoteActionSheet_AddNoteReminderMutation,
    NoteActionSheet_AddNoteReminderMutationVariables
  >(ADD_NOTE_ACTIVITY_REMINDER_MUTATION, noteReminder ? refetchQueries : {});

  const convertReminderToDate = (reminderDate: Date, reminderTime: Date) => {
    return new Date(
      reminderDate.getFullYear(),
      reminderDate.getMonth(),
      reminderDate.getDate(),
      reminderTime.getHours(),
      reminderTime.getMinutes(),
      0,
      0,
    );
  };

  const createNote = async () => {
    try {
      const noteActivity = await createNoteActivity({
        variables: {
          chatUUID,
          noteContent: note,
        },
      });

      if (!noteActivity?.data?.createNoteActivity?.crm_activity) {
        showErrorToast(i18n.t('new_note.error'));
        return false;
      }

      if (noteReminder && noteReminder.time) {
        const reminderDatetime = convertReminderToDate(
          noteReminder.date,
          noteReminder.time,
        ).toISOString();

        await createNoteReminder({
          variables: {
            chatUUID:
              noteActivity.data.createNoteActivity.crm_activity.chat_uuid,
            crmActivityUUID:
              noteActivity.data.createNoteActivity.crm_activity.uuid,
            reminderDatetime,
          },
        });
      }
      return true;
    } catch (e) {
      showErrorToast();
      return false;
    }
  };

  return {
    convertReminderToDate,
    createNote,
    loading: loading || (Boolean(noteReminder) && loadingReminder),
    showErrorToast,
  };
};
